import React, { useRef } from 'react';
import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
  Variants,
} from 'framer-motion';
import Connections from './Connections';
import Typewriter from './motion/Typewriter';

const container: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.15, delayChildren: 0.2 } },
};

const popIn: Variants = {
  hidden: { scale: 0.3, opacity: 0 },
  show: {
    scale: 1,
    opacity: 1,
    transition: { type: 'spring', stiffness: 200, damping: 18 },
  },
};

const rise: Variants = {
  hidden: { y: '0.6em', opacity: 0 },
  show: { y: 0, opacity: 1, transition: { duration: 0.5, ease: 'easeOut' } },
};

const headline = "Hello I'm Michael Ryan";

const Hero: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const reducedMotion = useReducedMotion();

  // Scroll parallax: layers move at different speeds while the hero scrolls
  // out of view. MotionConfig's reducedMotion does not cover style bindings,
  // so reduced motion is gated explicitly.
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '15%']);
  const blobY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const contentScale = useTransform(scrollYProgress, [0, 1], [1, 0.94]);

  return (
    <section ref={sectionRef} id="main" className="relative overflow-hidden py-8 bg-primary">
      {/* Background layer, oversized so the parallax slide never reveals a gap */}
      <motion.div
        aria-hidden="true"
        className="absolute -inset-y-[10%] inset-x-0 z-10 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: "url('/assets/img/bg-hero.jpg')",
          ...(reducedMotion ? {} : { y: bgY }),
        }}
      />
      <div className="absolute inset-0 z-20 bg-gradient-to-r from-hero-gradient-from to-hero-gradient-to"></div>

      {/* Aurora blobs — transform/opacity only, gated behind motion-safe */}
      <motion.div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-20 overflow-hidden"
        style={reducedMotion ? undefined : { y: blobY }}
      >
        <div className="absolute -left-24 top-1/4 h-80 w-80 rounded-full bg-yellow/20 blur-3xl motion-safe:animate-blob"></div>
        <div className="absolute -right-20 bottom-10 h-96 w-96 rounded-full bg-[#7C3AED]/30 blur-3xl motion-safe:animate-blob-slow"></div>
        <div className="absolute left-1/2 top-1/3 h-40 w-40 rounded-full bg-white/10 blur-2xl motion-safe:animate-blob"></div>
      </motion.div>

      <motion.div
        className="container relative z-30 pt-20 pb-12 sm:pt-56 sm:pb-48 lg:pt-64 lg:pb-48"
        style={reducedMotion ? undefined : { y: contentY, opacity: contentOpacity, scale: contentScale }}
      >
        <motion.div
          className="flex flex-col items-center justify-center lg:flex-row"
          initial="hidden"
          animate="show"
          variants={container}
        >
          <motion.div variants={popIn}>
            {/* Float lives on its own element so the CSS keyframe transform
                never collides with framer's spring transform above */}
            <div className="rounded-full border-8 border-primary shadow-xl motion-safe:animate-float">
              <img src="/assets/pp.jpg" className="h-48 rounded-full sm:h-56" alt="Author" />
            </div>
          </motion.div>
          <motion.div
            className="pt-8 sm:pt-10 lg:pl-8 lg:pt-0"
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08 } } }}
          >
            <h1 className="text-center font-header text-4xl text-white sm:text-left sm:text-5xl md:text-6xl">
              {headline.split(' ').map((word, i) => (
                <motion.span key={i} className="inline-block whitespace-pre" variants={rise}>
                  {word + ' '}
                </motion.span>
              ))}
            </h1>
            {/* Fixed height so typing/deleting never shifts layout */}
            <motion.div className="h-9 text-center sm:h-12 sm:text-left" variants={rise}>
              <Typewriter
                words={['Staff Engineer', 'Backend Architect', 'Tech Lead', 'Building with LLMs']}
                className="font-header text-2xl text-yellow sm:text-3xl"
              />
            </motion.div>
            <motion.div
              className="flex flex-col justify-center pt-3 sm:flex-row sm:pt-5 lg:justify-start"
              variants={rise}
            >
              <div className="flex items-center justify-center sm:justify-start md:pl-1">
                <p className="font-body text-lg uppercase text-white">Let's connect</p>
                <div className="hidden sm:block">
                  <i className="bx bx-chevron-right text-3xl text-yellow"></i>
                </div>
              </div>
              <div className="flex items-center justify-center pt-5 pl-2 sm:justify-start sm:pt-0 text-white">
                <Connections />
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
